import React from 'react';
import { CmsTraveller } from '../../integrations/contentful/types';
import {
  baseXInPixels,
  baseXUnit,
  breakpoints,
  imageHoverOpacity,
  linkStyle,
  spacing
} from '../../styles/theme';
import EllipsisOverflow from '../common/EllipsisOverflow';
import H from '../elements/H';
import { t } from '../../utils/i18n';
import { Paths } from '../../urls/Paths';
import RouteLink from '../navigation/RouteLink';
import Paragraph from '../elements/Paragraph';
import Spacing from '../common/Spacing';
import {
  propToResponsivePropObject,
  ResponsiveProp
} from '../../utils/layoutUtils';
import CmsMedia from '../cms/CmsMedia';
import HyperLink from '../elements/HyperLink';

interface CmsImageProps {
  traveller: CmsTraveller;
  imageSize: ResponsiveProp<number>;
}

const TravellerCard: React.FunctionComponent<CmsImageProps> = ({
  traveller,
  imageSize
}) => {
  const imageSizePx = baseXInPixels(
    Array.isArray(imageSize) ? Math.max(...imageSize.map(Number)) : imageSize
  );
  const imageSizeUnits =
    propToResponsivePropObject(imageSize).mapDefined(baseXUnit);
  return (
    <div data-cy="traveller-card">
      <div className="heading">
        <H styling="h5" color="gray">
          {t('Matkaaja')}
        </H>
      </div>
      <RouteLink
        path={Paths.Traveller}
        params={{
          slug: traveller.fields.slug
        }}
      >
        <HyperLink data-testid="traveller-link" noLinkStateStyles>
          <div className="card">
            <div className="picture">
              {traveller.fields.profilePicture && (
                <CmsMedia
                  media={traveller.fields.profilePicture}
                  width={imageSizePx}
                  height={imageSizePx}
                  borderRadius={1}
                  crop
                />
              )}
            </div>
            <div className="content">
              <Spacing left={[0, 0, 1]}>
                <span className="traveller-name">
                  <H styling="h4">
                    <strong>{traveller.fields.title}</strong>
                  </H>
                </span>

                <Paragraph small>
                  <EllipsisOverflow>
                    {traveller.fields.shortInfo}
                  </EllipsisOverflow>
                </Paragraph>
              </Spacing>
            </div>
          </div>
        </HyperLink>
      </RouteLink>
      {/*language=CSS*/}
      <style jsx>{`
        .heading {
          margin-bottom: ${spacing.half};
        }
        .card {
          margin: -1px;
          padding-top: ${spacing.one};
          display: flex;
        }
        .picture {
          flex-shrink: 0;
          margin-right: ${spacing.one};
          width: ${imageSizeUnits.small};
          height: ${imageSizeUnits.small};
        }
        .content {
          flex-grow: 1;
          min-width: 0;
          align-self: center;
        }
        .card:hover :global(img) {
          opacity: ${imageHoverOpacity};
        }
        .card .traveller-name {
          ${linkStyle.withAnimation}
        }
        .card:hover .traveller-name {
          ${linkStyle.hover}
        }
        .card:active .traveller-name {
          ${linkStyle.active}
        }
        @media ${breakpoints.medium} {
          .picture {
            width: ${imageSizeUnits.medium};
            height: ${imageSizeUnits.medium};
          }
        }
        @media ${breakpoints.large} {
          .picture {
            width: ${imageSizeUnits.large};
            height: ${imageSizeUnits.large};
          }
        }
      `}</style>
    </div>
  );
};

export default TravellerCard;
